import React from 'react';
import { getCurrentCopy } from '../../i18n/current';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

interface ModuleErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ModuleErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ModuleErrorBoundary extends React.Component<ModuleErrorBoundaryProps, ModuleErrorBoundaryState> {
  constructor(props: ModuleErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ModuleErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ModuleError]', error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo?.componentStack } } });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] py-10 px-5 text-center text-[var(--color-text-secondary)]">
          <AlertTriangle size={48} className="text-[var(--color-danger)] mb-4" />
          <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">{getCurrentCopy().common.errorBoundary.title}</h3>
          <p className="text-sm mb-5 max-w-[400px]">
            {getCurrentCopy().common.errorBoundary.description}
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="bg-[var(--color-bg-secondary)] py-3 px-4 rounded-[var(--radius-md)] text-xs text-[var(--color-danger)] max-w-[500px] overflow-auto mb-5 text-left">
              {this.state.error.toString()}
            </pre>
          )}
          <Button variant="primary" size="md" onClick={this.handleRetry}>
            <RefreshCw size={16} />
            {getCurrentCopy().common.actions.retry}
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ModuleErrorBoundary;
